import React, { useEffect, useMemo, useState } from 'react';
import { useTable, useGlobalFilter, useFilters, useSortBy, usePagination } from 'react-table';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Accordion } from 'react-bootstrap';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Select from 'react-select';
import axios from 'axios';
import swal from 'sweetalert';
import LoadingOverlay from 'react-loading-overlay-ts';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-daterangepicker/daterangepicker.css';
import { COLUMNS } from '../../Filters/DebitNote/DebitNoteColumns';
import '../../Filters/Common/filtering.css';
import ERROR from '../../../components/Validation/ErrorMessage';
import ERROR_MESSAGE from '../../Validation/ErrorMessage';
import COMMON_API from '../../../Api/CommonApi';
import DEBIT_NOTE_API from '../../../Api/DebitNoteApi';
import COMMONVARIABLES from '../../Utilities/constants';
import { getLoggedInUser, getRefreshToken } from '../../../../services/AuthService';
import { Logout } from '../../../../store/actions/AuthActions';

export const DebitNoteList = () => {
	const navigate = useNavigate();
	const BASE_URL = process.env.REACT_APP_BASE_URL;
	const columns = useMemo(() => COLUMNS, []);
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [supplierOptions, setSupplierOptions] = useState([{ label: 'All', value: 0 }]);
	const [supplierName, setSupplierName] = useState(
		JSON.parse(localStorage.getItem('debitNoteSupplier')) || { label: 'All', value: 0 }
	);
	const [itemName, setItemName] = useState(() => {
		const saved = localStorage.getItem('debitNoteItemName');
		if (!saved || saved === 'null') return '';
		try {
			const parsed = JSON.parse(saved);
			// Old filter stored Select object { label, value }
			if (parsed && typeof parsed === 'object') return '';
			return String(parsed || '');
		} catch {
			return saved;
		}
	});
	const [range, setRange] = useState(localStorage.getItem('debitNoteRange') || null);

	// Same allow list as Debit Note Add item name text box
	const sanitizeItemName = (value) =>
		String(value || '')
			.replace(/[^A-Za-z0-9.\-/&, ]/g, '')
			.replace(/\s+/g, ' ');

	const start_date =
		localStorage.getItem('debitNoteRange') &&
		new Date(localStorage.getItem('debitNoteRange').split('-')[0]).toDateString();
	const end_date =
		localStorage.getItem('debitNoteRange') &&
		new Date(localStorage.getItem('debitNoteRange').split('-')[1]).toDateString();
	const [rangeDate] = useState(
		localStorage.getItem('debitNoteRange') && localStorage.getItem('debitNoteRange') !== 'null'
			? { startDate: new Date(start_date), endDate: new Date(end_date) }
			: { startDate: new Date(), endDate: new Date() }
	);

	const tableInstance = useTable(
		{ columns, data, initialState: { pageIndex: 0 } },
		useFilters,
		useGlobalFilter,
		useSortBy,
		usePagination
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		state,
		page,
		gotoPage,
		pageCount,
		pageOptions,
		nextPage,
		previousPage,
		canNextPage,
		canPreviousPage,
	} = tableInstance;

	const { pageIndex } = state;

	useEffect(() => {
		fetchMasterData();
		bindDebitNoteData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	async function fetchMasterData() {
		try {
			const user = getLoggedInUser();
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
			const listParams = { status_code: 0, user_id: user?.id };
			const supplierData = await axios.post(
				BASE_URL + COMMON_API.SUPPLIER_MASTER_LIST,
				listParams,
				config
			);

			if (supplierData?.data?.body?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
				const refresh = await getRefreshToken();
				if (refresh) {
					if (Number(refresh) === COMMONVARIABLES.STATUS.EXPIRED_TOKEN) {
						Logout();
					} else {
						await fetchMasterData();
					}
				}
				return;
			}

			if (supplierData?.data?.body?.supplierList) {
				setSupplierOptions([
					{ label: 'All', value: 0 },
					...supplierData.data.body.supplierList.map((option) => ({
						label: option.sm_supplier_name,
						value: option.sm_supplier_code,
					})),
				]);
			}
		} catch (error) {
			console.error('Error fetching debit note filters:', error);
		}
	}

	const formatDateYMD = (value) => {
		const date = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(date.getTime())) return '';
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const d = String(date.getDate()).padStart(2, '0');
		return `${y}-${m}-${d}`;
	};

	const formatDateDMY = (value) => {
		// Display as DD/MM/YYYY
		if (!value) return '';
		const dateStr = String(value).trim();
		if (dateStr.includes('/')) {
			const parts = dateStr.split('/');
			if (parts.length === 3) {
				return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
			}
			return dateStr;
		}
		if (dateStr.includes('-') && dateStr.indexOf('-') === 2) {
			// already DD-MM-YYYY
			const parts = dateStr.split('-');
			if (parts.length === 3 && parts[0].length <= 2) {
				return `${parts[0].padStart(2, '0')}/${parts[1].padStart(2, '0')}/${parts[2]}`;
			}
		}
		if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
			const [y, m, d] = dateStr.slice(0, 10).split('-');
			return `${d}/${m}/${y}`;
		}
		const date = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(date.getTime())) return dateStr;
		const y = date.getFullYear();
		const m = String(date.getMonth() + 1).padStart(2, '0');
		const d = String(date.getDate()).padStart(2, '0');
		return `${d}/${m}/${y}`;
	};

	async function bindDebitNoteData() {
		try {
			setIsLoading(true);
			const user = getLoggedInUser();
			let varFromDate = formatDateYMD(new Date());
			let varToDate = formatDateYMD(new Date());
			if (range != null && range !== 'null') {
				varFromDate = formatDateYMD(range.split('-')[0]);
				varToDate = formatDateYMD(range.split('-')[1]);
			}

			const filterItemName = sanitizeItemName(itemName).trim();
			localStorage.setItem('debitNoteRange', range);
			localStorage.setItem('debitNoteSupplier', JSON.stringify(supplierName));
			localStorage.setItem('debitNoteItemName', filterItemName);

			// List contract — item_name: string | null
			const params = {
				user_id: Number(user?.id),
				supplier_id: Number(supplierName?.value || 0),
				item_name: filterItemName === '' ? null : filterItemName,
				from_date: varFromDate,
				to_date: varToDate,
			};
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
			const listRes = await axios.post(BASE_URL + DEBIT_NOTE_API.DEBIT_NOTE_LIST, params, config);
			setIsLoading(false);

			if (
				listRes?.data?.body?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED ||
				listRes?.data?.status === COMMONVARIABLES.STATUS.UNAUTHORIZED
			) {
				const refresh = await getRefreshToken();
				if (refresh) {
					if (Number(refresh) === COMMONVARIABLES.STATUS.EXPIRED_TOKEN) {
						Logout();
					} else {
						await bindDebitNoteData();
					}
				}
				return;
			}

			if (
				listRes?.data?.body?.status === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS ||
				listRes?.data?.status === COMMONVARIABLES.STATUS.SERVICE_CATCH_STATUS
			) {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
				setData([]);
				return;
			}

			const rawList =
				listRes?.data?.body?.debitNoteList ||
				listRes?.data?.body?.list ||
				listRes?.data?.body?.data ||
				[];

			const list = (Array.isArray(rawList) ? rawList : []).map((row) => {
				const id = row.dn_debit_note_id ?? row.debit_note_id ?? row.transaction_no;
				const dateValue = formatDateDMY(row.debit_note_date || row.dn_debit_note_date || '');
				return {
					...row,
					debit_note_id: id,
					transaction_no: id,
					debit_note_date: dateValue,
					debit_note_no: row.dn_debit_note_number ?? row.debit_note_number ?? row.debit_note_no,
					supplier_name: row.sm_supplier_name || row.supplier_name || '',
					item_name: row.item_name || row.im_display_name || '',
					sub_total: row.dn_sub_total ?? row.sub_total ?? 0,
					gst_percent: row.dn_gst_percent ?? row.gst_percent ?? 5,
					gst_amount: row.dn_gst_amount ?? row.gst_amount ?? 0,
					total_amount: row.dn_total_amount ?? row.total_amount ?? 0,
					narration: row.dn_narration || row.narration || '',
				};
			});
			setData(list);
		} catch (error) {
			setIsLoading(false);
			setData([]);
			console.error('Error fetching debit note list:', error);
			if (error.code === COMMONVARIABLES.STATUS.NETWORK_ERROR_STATUS) {
				toast.error(ERROR_MESSAGE.API_ERROR.NETWORK_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		}
	}

	const udfnEdit = (e, debitNoteId) => {
		e?.preventDefault?.();
		const id = Number(debitNoteId);
		if (!id) {
			toast.error('Invalid debit note id', { position: toast.POSITION.TOP_RIGHT });
			return;
		}
		navigate('/debit-note-add', { state: { transNo: id, debit_note_id: id } });
	};

	const udfnDeleteConfirm = (transNo, debitNoteNo) => {
		swal({
			title: 'Are you sure?',
			text: `Delete debit note ${debitNoteNo || ''}?`,
			icon: 'warning',
			buttons: true,
			dangerMode: true,
		}).then((willDelete) => {
			if (willDelete) {
				udfnDelete(transNo);
			}
		});
	};

	const udfnDelete = async (transNo) => {
		try {
			setIsLoading(true);
			const user = getLoggedInUser();
			const config = { headers: { authorization: 'Bearer '.concat(user.accessToken) } };
			const deleteRes = await axios.post(
				BASE_URL + DEBIT_NOTE_API.DELETE_DEBIT_NOTE,
				{
					debit_note_id: Number(transNo),
					user_id: Number(user?.id),
					company_code: Number(user?.company_code),
					fy: Number(localStorage.getItem('fy') || 0),
				},
				config
			);
			setIsLoading(false);

			const deleteStatus = deleteRes?.data?.body?.status ?? deleteRes?.data?.status;
			if (deleteStatus === COMMONVARIABLES.STATUS.UNAUTHORIZED) {
				const refresh = await getRefreshToken();
				if (refresh) {
					if (Number(refresh) === COMMONVARIABLES.STATUS.EXPIRED_TOKEN) {
						Logout();
					} else {
						await udfnDelete(transNo);
					}
				}
				return;
			}

			if (deleteStatus === COMMONVARIABLES.STATUS.SUCCESS) {
				swal('Deleted!', 'Debit note deleted successfully.', 'success');
				await bindDebitNoteData();
			} else if (deleteRes?.data?.body?.message) {
				toast.error(deleteRes.data.body.message, { position: toast.POSITION.TOP_RIGHT });
			} else {
				toast.error(ERROR.API_ERROR.SERVICE_CATCH_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		} catch (error) {
			setIsLoading(false);
			if (error.code === COMMONVARIABLES.STATUS.NETWORK_ERROR_STATUS) {
				toast.error(ERROR_MESSAGE.API_ERROR.NETWORK_ERROR, { position: toast.POSITION.TOP_RIGHT });
			}
		}
	};

	return (
		<>
			<LoadingOverlay active={isLoading} spinner text="Loading ...">
				<div className="card">
					<div className="card-header d-flex justify-content-between align-items-center">
						<h4 className="card-title mb-0">Debit Note List</h4>
						<Link to="/debit-note-add" className="btn btn-primary">
							<i className="fa fa-plus" /> &nbsp;Add Debit Note
						</Link>
					</div>
					<div className="card-body">
						<Accordion
							className="accordion accordion-rounded-stylish accordion-bordered accordion-active-header"
							defaultActiveKey="0"
						>
							<Accordion.Item>
								<Accordion.Header className="accordion-header accordion-header-primary">
									<h5>Filters</h5>
									<span className="accordion-header-indicator" />
								</Accordion.Header>
								<Accordion.Collapse className="accordion__body">
									<div className="accordion-body">
										<div className="row">
											<div className="col-md-4 mb-3">
												<div className="example rangeDatePicker">
													<p className="mb-1">Select From Date And To Date</p>
													<DateRangePicker
														onApply={(e) => setRange(e.target.value)}
														initialSettings={rangeDate}
													>
														<input
															type="text"
															className="form-control input-daterange-timepicker"
														/>
													</DateRangePicker>
												</div>
											</div>
											<div className="col-md-3 mb-3">
												<p className="mb-1">Supplier</p>
												<Select
													value={supplierName}
													onChange={setSupplierName}
													options={supplierOptions}
													className="my-react-select-container"
													theme={(theme) => ({
														...theme,
														borderRadius: 0,
														colors: {
															...theme.colors,
															primary25: '#b3b3b3',
															primary: 'black',
															neutral80:
																'data-theme-version' === 'dark' ? 'white' : '',
														},
													})}
												/>
											</div>
											<div className="col-md-3 mb-3">
												<p className="mb-1">Item Name</p>
												<input
													type="text"
													className="form-control"
													placeholder="Item Name"
													value={itemName}
													maxLength={50}
													onChange={(e) => setItemName(sanitizeItemName(e.target.value))}
												/>
											</div>
											<div className="col-md-2 mt-3">
												<Button
													className="me-2"
													variant="whatsapp"
													style={{ marginTop: '6%' }}
													onClick={() => bindDebitNoteData()}
												>
													List{' '}
													<span className="btn-icon-right">
														<i className="fa fa-filter" />
													</span>
												</Button>
											</div>
										</div>
									</div>
								</Accordion.Collapse>
							</Accordion.Item>
						</Accordion>

						<div className="table-responsive">
							<table {...getTableProps()} className="table filtering-table table-responsive-lg">
								<thead>
									{headerGroups.map((headerGroup, index) => (
										<tr {...headerGroup.getHeaderGroupProps()} key={index}>
											<th>
												<p className="tbl-heading-txt">S.NO.</p>
											</th>
											{headerGroup.headers.map((column, colIndex) => (
												<th
													key={colIndex}
													className={
														column.Header === 'Subtotal' ||
														column.Header === 'GST' ||
														column.Header === 'Grand Total'
															? 'text-end'
															: column.Header === 'Debit Note No.'
															? 'text-center'
															: ''
													}
												>
													{column.render('Header')}
													{column.canFilter ? column.render('Filter') : null}
												</th>
											))}
											<th>
												<p className="tbl-heading-txt">Actions</p>
											</th>
										</tr>
									))}
								</thead>
								<tbody {...getTableBodyProps()}>
									{page.length > 0 ? (
										page.map((row, index) => (
											<tr key={index}>
												<td>{pageIndex * 10 + index + 1}</td>
												<td>{row.original.debit_note_date}</td>
												<td className="text-center">{row.original.debit_note_no}</td>
												<td>{row.original.supplier_name}</td>
												<td className="text-end">
													{COMMONVARIABLES.India_Rupee_Formatter_Digits(
														row.original.sub_total || 0
													)}
												</td>
												<td className="text-end">
													{COMMONVARIABLES.India_Rupee_Formatter_Digits(
														row.original.gst_amount || 0
													)}{' '}
													<span className="text-muted">
														@{row.original.gst_percent ?? 5}%
													</span>
												</td>
												<td className="text-end">
													{COMMONVARIABLES.India_Rupee_Formatter_Digits(
														row.original.total_amount || 0
													)}
												</td>
												<td>{row.original.narration}</td>
												<td>
													<div className="d-flex">
														<Link
															to="#"
															className="btn btn-secondary shadow btn-xs sharp me-1"
															onClick={(e) =>
																udfnEdit(
																	e,
																	row.original.dn_debit_note_id ||
																		row.original.debit_note_id ||
																		row.original.transaction_no
																)
															}
														>
															<i className="fa fa-pencil" />
														</Link>
														<Link
															to="#"
															className="btn btn-danger shadow btn-xs sharp"
															onClick={(e) => {
																e.preventDefault();
																udfnDeleteConfirm(
																	row.original.dn_debit_note_id ||
																		row.original.debit_note_id ||
																		row.original.transaction_no,
																	row.original.debit_note_no ||
																		row.original.dn_debit_note_number
																);
															}}
														>
															<i className="fa fa-trash" />
														</Link>
													</div>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan={9} className="text-center">
												No records found
											</td>
										</tr>
									)}
								</tbody>
							</table>
							<div className="d-flex justify-content-between">
								<span>
									Page{' '}
									<strong>
										{pageIndex + 1} of {pageOptions.length || 1}
									</strong>
								</span>
								<span className="table-index">
									Go to page:{' '}
									<input
										type="number"
										className="ml-2"
										defaultValue={pageIndex + 1}
										onChange={(e) => {
											const pageNumber = e.target.value ? Number(e.target.value) - 1 : 0;
											gotoPage(pageNumber);
										}}
									/>
								</span>
							</div>
							<div className="text-center mb-3">
								<div className="filter-pagination mt-3">
									<button
										className="previous-button"
										onClick={() => gotoPage(0)}
										disabled={!canPreviousPage}
									>
										{'<<'}
									</button>
									<button
										className="previous-button"
										onClick={() => previousPage()}
										disabled={!canPreviousPage}
									>
										Previous
									</button>
									<button
										className="next-button"
										onClick={() => nextPage()}
										disabled={!canNextPage}
									>
										Next
									</button>
									<button
										className="next-button"
										onClick={() => gotoPage(pageCount - 1)}
										disabled={!canNextPage}
									>
										{'>>'}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</LoadingOverlay>
		</>
	);
};

export default DebitNoteList;
